import { useEffect, useRef } from 'react';
import { SocketConnection } from '../socket/socket-main-thread/socket-connection';
import socketManager from '../socket/socket-main-thread/socket-manager';
import { GateTypes, Requirement, UploadListCompletePayload } from '@/types/registration.types';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchPasscodeLists } from '@/store/slices/passcode-lists';
import { saveWorkingProjectByFields } from '@/store/slices/project';
import { selectWorkingProjectRegistrationSettings, getWorkingProjectSessions } from '@/store/slices/project/selectors';
import { useProjectUuidParam } from '@/utils/use-project-uuid-param';
import toastTrigger from '@ui/toast/toast-trigger';
import { EToastTypes } from '@ui/toast/toast.types';

export default function useUploadListComplete(channel: number | null | undefined) {
  const dispatch = useAppDispatch();
  const projectUuidParam = useProjectUuidParam();
  const registrationSettings = useAppSelector(selectWorkingProjectRegistrationSettings);
  const sessions = useAppSelector(getWorkingProjectSessions);
  const socket = useRef<SocketConnection | null>(null);

  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;
  const projectUuidParamRef = useRef(projectUuidParam);
  projectUuidParamRef.current = projectUuidParam;
  const channelRef = useRef(channel);
  channelRef.current = channel;
  const registrationSettingsRef = useRef(registrationSettings);
  registrationSettingsRef.current = registrationSettings;
  const sessionsRef = useRef(sessions);
  sessionsRef.current = sessions;

  useEffect(() => {
    if (!channel) return;

    const namespace = `admin-${channel}`;
    socket.current = socketManager.getFromHook(namespace);

    const handleComplete = (response: UploadListCompletePayload) => {
      if (response.data.eventUuid !== projectUuidParamRef.current) return;

      toastTrigger('Audience list upload has finished', { type: EToastTypes.success, autoClose: 5000 });

      const { failedEmailCount, passcodeListId } = response.data;

      if (failedEmailCount) {
        toastTrigger(`${failedEmailCount} email${failedEmailCount === 1 ? '' : 's'} were invalid and skipped.`, {
          type: EToastTypes.error,
          autoClose: 5000,
        });
      }

      const settings = registrationSettingsRef.current;
      if (passcodeListId && settings) {
        const ch = channelRef.current;
        if (ch) dispatchRef.current(fetchPasscodeLists(ch));

        const sessionsIDs = sessionsRef.current?.map((s) => s.session) ?? [];
        const workingGateRequirement: Requirement = {
          type: GateTypes.passcode_list,
          passcodeList: [passcodeListId],
          gatedSessions: [...sessionsIDs],
        };
        const listsCopy = [...(settings.requirements?.[GateTypes.passcode_list] ?? [])];
        listsCopy.push(workingGateRequirement);

        dispatchRef.current(
          saveWorkingProjectByFields({
            registration_settings: {
              ...settings,
              requirements: {
                ...settings.requirements,
                [GateTypes.passcode_list]: listsCopy,
              },
            },
          })
        );
      }
    };

    socket.current.addListener<UploadListCompletePayload>('upload-list-complete', handleComplete);

    return () => {
      socket.current?.removeListener('upload-list-complete', handleComplete);
      socketManager.leave(namespace);
    };
  }, [channel]);
}
